import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProgressTracker = ({ challenge, progress, onUpdate }) => {
  const [newValue, setNewValue] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  const handleUpdateProgress = async () => {
    if (!newValue || isNaN(newValue)) {
      setError('Please enter a valid number');
      return;
    }

    setUpdating(true);
    setError('');

    try {
      const updatedValue = progress?.current_value + parseFloat(newValue);
      const isCompleted = updatedValue >= challenge?.target_value;

      const { error: updateError } = await supabase
        ?.from('user_challenge_progress')
        ?.update({
          current_value: updatedValue,
          is_completed: isCompleted,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', progress?.id);

      if (updateError) throw updateError;

      setNewValue('');
      onUpdate();
    } catch (err) {
      setError(err?.message || 'Failed to update progress');
    } finally {
      setUpdating(false);
    }
  };

  const progressPercentage = Math.min(
    (progress?.current_value / challenge?.target_value) * 100,
    100
  );

  return (
    <div className="bg-muted/30 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
        Update Your Progress
      </h3>

      {/* Current Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">Current Progress</span>
          <span className="font-semibold text-foreground">
            {progress?.current_value} / {challenge?.target_value} {challenge?.metric_unit}
          </span>
        </div>
        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <div className="text-right mt-1">
          <span className="text-xs text-muted-foreground">
            {progressPercentage?.toFixed(1)}% Complete
          </span>
        </div>
      </div>

      {/* Add Progress */}
      {!progress?.is_completed && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Add Progress ({challenge?.metric_unit})
          </label>
          <div className="flex gap-3">
            <Input
              type="number"
              value={newValue}
              onChange={(e) => setNewValue(e?.target?.value)}
              placeholder={`Enter ${challenge?.metric_unit}`}
              className="flex-1"
              disabled={updating}
            />
            <Button
              onClick={handleUpdateProgress}
              disabled={!newValue || updating}
              className="flex-shrink-0"
            >
              {updating ? (
                <Icon name="Loader2" size={18} className="animate-spin" />
              ) : (
                'Update'
              )}
            </Button>
          </div>
          {error && (
            <p className="mt-2 text-sm text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={16} />
              {error}
            </p>
          )}
        </div>
      )}

      {/* Completion Badge */}
      {progress?.is_completed && (
        <div className="flex items-center gap-3 p-4 bg-green-100 text-green-700 rounded-lg">
          <Icon name="CheckCircle" size={24} />
          <div>
            <h4 className="font-semibold">Challenge Completed!</h4>
            <p className="text-sm">Congratulations on achieving your goal</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;